import React, { memo, useMemo } from "react";
import { Center } from "@chakra-ui/react";
import colors from "app/colors";
import {
  AudioFileIcon,
  DocumentFileFooterIcon,
  ImageFileFooterIcon,
  PdfFileFooterIcon,
  PowerPointFileFooterIcon,
  SpreadsheetFileFooterIcon,
  UnknownFileFooterIcon,
  VideoFileFooterIcon,
  ZipFileFooterIcon,
} from "modules/icons";
import { isAudio, isImage, isPdf, isVideo } from "modules/utils/Functions";

interface IProps {
  fill: string;
  contentType: string;
  radius?: number;
  size: number;
}

interface IFileFooterIcon {
  icon: React.ReactNode;
  color: string;
}

const FileFooterIcon = ({ fill, contentType, radius, size }: IProps) => {
  const boxSize = 4;

  const footerIcon = useMemo((): IFileFooterIcon => {
    if (isImage(contentType)) {
      return {
        icon: <ImageFileFooterIcon boxSize={boxSize} />,
        color: colors.blackSecondary,
      };
    }
    if (isAudio(contentType)) {
      return {
        icon: <AudioFileIcon boxSize={boxSize} />,
        color: colors.blackSecondary,
      };
    }
    if (isVideo(contentType)) {
      return {
        icon: <VideoFileFooterIcon boxSize={boxSize} />,
        color: colors.blackSecondary,
      };
    }
    if (isPdf(contentType)) {
      return {
        icon: <PdfFileFooterIcon boxSize={boxSize} />,
        color: colors.red,
      };
    }
    if (
      contentType === "application/vnd.ms-excel" ||
      contentType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return {
        icon: <SpreadsheetFileFooterIcon boxSize={boxSize} />,
        color: colors.greenPrimary,
      };
    }
    if (
      contentType === "application/msword" ||
      contentType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return {
        icon: <DocumentFileFooterIcon boxSize={boxSize} />,
        color: colors.blue,
      };
    }
    if (
      contentType ===
        "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      contentType === "application/vnd.ms-powerpoint"
    ) {
      return {
        icon: <PowerPointFileFooterIcon boxSize={boxSize} />,
        color: colors.blue,
      };
    }
    if (
      contentType === "application/zip" ||
      contentType === "application/x-zip-compressed"
    ) {
      return {
        icon: <ZipFileFooterIcon boxSize={boxSize} />,
        color: colors.blueZip,
      };
    }
    return {
      icon: <UnknownFileFooterIcon boxSize={boxSize} />,
      color: colors.blackSecondary,
    };
  }, [contentType, boxSize]);

  if (!footerIcon) {
    return null;
  }

  return (
    <Center
      fill={fill}
      bg={footerIcon.color}
      alignContent="center"
      borderBottomRightRadius={radius}
      borderTopLeftRadius={radius}
      w={size}
      h={size}
    >
      {footerIcon.icon}
    </Center>
  );
};

export default memo(FileFooterIcon);
